/** Bell popover that persists facility-alert acknowledgements with optimistic rollback and server actor attribution. */
import { useCallback, useMemo, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { Badge } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { acknowledgeAlert } from '@/api/alerts';
import { useToast } from '@/components/common/ToastProvider';
import type { RootState } from '@/store';
import {
  selectUnacknowledgedAlerts,
  setAlertAcknowledgement,
} from '@/store/slices/realtimeSlice';
import type { Alert } from '@/types';

interface NotificationCenterProps {
  facilityId?: string;
}

type Severity = Alert['severity'];

const MAX_BULK_ACKNOWLEDGEMENTS = 20;

const SEVERITY_COLOR: Record<Severity, string> = {
  critical: 'error.main',
  high: 'warning.main',
  medium: 'info.main',
  low: 'success.main',
};

function SeverityIcon({ severity }: { severity: Severity }): ReactElement {
  const sx = { color: SEVERITY_COLOR[severity], fontSize: 20 };
  switch (severity) {
    case 'critical': return <ErrorIcon sx={sx} />;
    case 'high':     return <WarningIcon sx={sx} />;
    case 'medium':   return <InfoIcon sx={sx} />;
    case 'low':      return <CheckCircleIcon sx={sx} />;
  }
}

function relativeTime(ts: number): string {
  return formatDistanceToNow(new Date(ts), { addSuffix: true });
}

function acknowledgementTimestamp(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value !== 'string' && !(value instanceof Date)) return undefined;
  const timestamp = Date.parse(value instanceof Date ? value.toISOString() : value);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : undefined;
}

const ALERT_TYPE_LABEL: Record<Alert['alert_type'], string> = {
  worker_down:       'Worker Down',
  hazard_zone:       'Hazard Zone',
  unsafe_gesture:    'Unsafe Gesture',
  sensor_disconnect: 'Sensor Disconnect',
  sos:               'SOS',
  proximity_warning: 'Proximity Warning',
  safety_light_change: 'Safety Light Change',
};

export default function NotificationCenter({ facilityId }: NotificationCenterProps): ReactElement {
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const toast      = useToast();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const pendingIdsRef = useRef(new Set<string>());
  const bulkRunningRef = useRef(false);

  // Last 20 alerts, sorted by timestamp descending (already newest-first from addAlert)
  const allAlerts = useSelector((state: RootState) => state.realtime.alerts);
  const unread    = useSelector(selectUnacknowledgedAlerts);
  const actorId   = useSelector((state: RootState) => state.auth.user?.id);

  const recent = useMemo(
    () => [...allAlerts].sort((a, b) => b.timestamp - a.timestamp).slice(0, 20),
    [allAlerts],
  );

  const unreadCount = unread.length;

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchor(null), []);

  const setAlertPending = useCallback((alertId: string, pending: boolean) => {
    if (pending) {
      pendingIdsRef.current.add(alertId);
    } else {
      pendingIdsRef.current.delete(alertId);
    }
    setPendingIds(new Set(pendingIdsRef.current));
  }, []);

  const persistAcknowledgement = useCallback(async (
    alert: Alert,
    reportError: boolean,
  ): Promise<boolean> => {
    if (!facilityId || alert.acknowledged || alert.resolved) return Boolean(facilityId);
    if (pendingIdsRef.current.has(alert.id)) return false;

    const snapshot = {
      acknowledged: alert.acknowledged,
      acknowledgedBy: alert.acknowledged_by,
      acknowledgedAt: alert.acknowledged_at,
    };
    const optimisticAt = Date.now();
    setAlertPending(alert.id, true);
    dispatch(setAlertAcknowledgement({
      alertId: alert.id,
      acknowledged: true,
      acknowledgedBy: actorId,
      acknowledgedAt: optimisticAt,
    }));

    try {
      const persisted = await acknowledgeAlert(facilityId, alert.id);
      const serverActor = typeof persisted.acknowledged_by === 'string'
        && persisted.acknowledged_by.trim()
        ? persisted.acknowledged_by
        : undefined;
      const serverAcknowledgedAt = acknowledgementTimestamp(persisted.acknowledged_at);
      if (
        persisted.id !== alert.id
        || persisted.acknowledged !== true
        || !serverActor
        || serverAcknowledgedAt === undefined
      ) {
        throw new Error('Alert acknowledgement response did not confirm the requested alert');
      }
      dispatch(setAlertAcknowledgement({
        alertId: alert.id,
        acknowledged: true,
        acknowledgedBy: serverActor,
        acknowledgedAt: serverAcknowledgedAt,
      }));
      return true;
    } catch {
      dispatch(setAlertAcknowledgement({
        alertId: alert.id,
        ...snapshot,
        expected: {
          acknowledged: true,
          acknowledgedBy: actorId,
          acknowledgedAt: optimisticAt,
        },
      }));
      if (reportError) {
        toast.error('Alert was not acknowledged. Check your connection and try again.');
      }
      return false;
    } finally {
      setAlertPending(alert.id, false);
    }
  }, [actorId, dispatch, facilityId, setAlertPending, toast]);

  const handleMarkAllRead = useCallback(async () => {
    if (!facilityId || bulkRunningRef.current || unread.length === 0) return;

    const targets = [...unread]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, MAX_BULK_ACKNOWLEDGEMENTS);
    let acknowledged = 0;
    bulkRunningRef.current = true;
    setBulkRunning(true);
    try {
      for (const alert of targets) {
        if (await persistAcknowledgement(alert, false)) acknowledged += 1;
      }
    } finally {
      bulkRunningRef.current = false;
      setBulkRunning(false);
    }

    const failed = targets.length - acknowledged;
    const remaining = unread.length - targets.length;
    if (failed > 0) {
      toast.error(
        `${acknowledged} of ${targets.length} alerts acknowledged. ${failed} failed; try again.`,
      );
    } else if (remaining > 0) {
      toast.info(
        `${acknowledged} alerts acknowledged. ${remaining} remain; run Mark all read again.`,
      );
    } else {
      toast.success(`${acknowledged} alerts acknowledged.`);
    }
  }, [facilityId, persistAcknowledgement, toast, unread]);

  const handleClickAlert = useCallback(
    (alert: Alert) => {
      if (!facilityId || bulkRunningRef.current || pendingIdsRef.current.has(alert.id)) return;
      if (!alert.acknowledged && !alert.resolved) {
        void persistAcknowledgement(alert, true);
      }
      handleClose();
      navigate(`/facilities/${facilityId}/alerts`);
    },
    [facilityId, handleClose, navigate, persistAcknowledgement],
  );

  const handleViewAll = useCallback(() => {
    handleClose();
    if (facilityId) {
      navigate(`/facilities/${facilityId}/alerts`);
    }
  }, [facilityId, handleClose, navigate]);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleOpen} aria-label="open notifications">
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 520 } } }}
      >
        {/* Header row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon fontSize="small" />}
              onClick={() => { void handleMarkAllRead(); }}
              disabled={!facilityId || bulkRunning}
              sx={{ textTransform: 'none' }}
            >
              {bulkRunning ? 'Acknowledging...' : 'Mark all read'}
            </Button>
          )}
        </Stack>

        <Divider />

        {/* Notification list */}
        {recent.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6, gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.6 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Stack>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', maxHeight: 380 }}>
            {recent.map((alert, idx) => {
              const isUnread = !alert.acknowledged && !alert.resolved;
              return (
                <Box key={alert.id}>
                  <ListItemButton
                    onClick={() => { void handleClickAlert(alert); }}
                    disabled={!facilityId || bulkRunning || pendingIds.has(alert.id)}
                    sx={{
                      px: 2,
                      py: 1,
                      alignItems: 'flex-start',
                      gap: 1.5,
                      bgcolor: isUnread ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                      <SeverityIcon severity={alert.severity} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography
                          variant="body2"
                          fontWeight={isUnread ? 'bold' : 'normal'}
                          noWrap
                          sx={{ maxWidth: 200 }}
                        >
                          {ALERT_TYPE_LABEL[alert.alert_type]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                          {relativeTime(alert.timestamp)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {alert.message}
                      </Typography>
                    </Box>
                  </ListItemButton>
                  {idx < recent.length - 1 && <Divider component="li" />}
                </Box>
              );
            })}
          </List>
        )}

        <Divider />

        {/* Footer */}
        <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
          <Button
            size="small"
            onClick={handleViewAll}
            disabled={!facilityId}
            sx={{ textTransform: 'none' }}
          >
            View all alerts
          </Button>
        </Box>
      </Popover>
    </>
  );
}
